import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import css from './MethodologyPage.module.css'

const sections = [
  {
    id: 'closed-fact', title: 'Закрытый факт',
    formula: 'Сумма закрытых сделок с датой отгрузки внутри выбранного периода ÷ план × 100%.',
    note: 'Для оборота берётся сумма сделки, для валовой прибыли — поле валовой прибыли.',
  },
  {
    id: 'calendar-pace', title: 'Календарный темп',
    formula: 'Прошедшие дни периода ÷ общее количество дней периода × 100%.',
    note: 'Показывает, какая доля плана должна быть выполнена к текущей дате при равномерном темпе.',
  },
  {
    id: 'forecast', title: 'Прогноз на конец периода',
    formula: 'Текущий процент выполнения ÷ доля уже прошедшего периода.',
    note: 'Это линейный прогноз: он показывает возможный итог, если текущий средний темп сохранится.',
  },
  {
    id: 'marginality', title: 'Маржинальность',
    formula: 'Прибыль для маржи по закрытым сделкам ÷ оборот закрытых сделок × 100%.',
    note: 'Используется специальное поле сделки «прибыль для маржи».',
  },
  {
    id: 'gross-profitability', title: 'Валовая рентабельность',
    formula: 'Валовая прибыль закрытых сделок ÷ их оборот × 100%.',
    note: 'Позволяет сравнивать качество результата независимо от размера оборота.',
  },
  {
    id: 'payment-rate', title: 'Оплачено',
    formula: 'Фактически оплаченная сумма по закрытым сделкам ÷ их оборот × 100%.',
    note: 'Рядом показывается абсолютная сумма оплаты.',
  },
  {
    id: 'average-deal', title: 'Сделки и средний чек',
    formula: 'Средний чек = оборот закрытых сделок ÷ количество закрытых сделок.',
    note: 'Учитываются только сделки выбранного периода и выбранного уровня: компания, отдел или сотрудник.',
  },
  {
    id: 'shipment-regularity', title: 'Регулярность отгрузок',
    formula: 'Считаются интервалы между датами отгрузки и дни от последней отгрузки до текущей даты.',
    note: 'Оценка «если бы регулярность сохранялась» продолжает средний темп до последней отгрузки до текущей даты.',
  },
  {
    id: 'pipeline', title: 'Воронка до отгрузки',
    formula: 'КП — 35%, счёт — 70%, к отгрузке — 95% от суммы или прибыли сделки.',
    note: 'Взвешенный потенциал является прогнозной оценкой и не входит в закрытый факт.',
  },
  {
    id: 'comparison', title: 'Аналогичный прошлый период',
    formula: 'Берётся тот же диапазон дат годом ранее и строится по тем же правилам.',
    note: 'Например, 01.07–30.09.2026 сравнивается с 01.07–30.09.2025.',
  },
  {
    id: 'contribution', title: 'Вклад сотрудников',
    formula: 'Сумма оборота и валовой прибыли закрытых сделок каждого ответственного.',
    note: 'Сделка относится к сотруднику, указанному ответственным в CRM.',
  },
  {
    id: 'products', title: 'Товары и категории',
    formula: 'Сумма товарной строки = цена × количество; затем строки группируются по товару и категории.',
    note: 'Итог товарных строк может отличаться от суммы сделки из-за ручных корректировок.',
  },
  {
    id: 'pricing', title: 'Отклонения цен',
    formula: 'Цена позиции сравнивается с медианной ценой такой же позиции в выбранных сделках.',
    note: '«Дешевле» и «дороже» показывают отклонения относительно ориентира, а баланс — их взаимную компенсацию. Это сигнал для проверки, не подтверждённый убыток.',
  },
  {
    id: 'snapshots', title: 'Срезы',
    formula: 'При фиксации сохраняются показатели и сделки завершённого периода на конкретный момент.',
    note: 'Аналитика срезов сравнивает сохранённое состояние с текущими данными CRM и показывает изменившуюся сделку и поле.',
  },
]

export default function MethodologyPage() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, behavior: 'smooth' })
    else document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [location.hash])

  return <main className={css.page}>
    <header className={css.header}>
      <button type="button" onClick={() => navigate(-1)}>← Назад</button>
      <span>Справка</span>
      <h1>Как считаются показатели</h1>
      <p>Краткая методика управленческой аналитики плана продаж.</p>
    </header>

    <nav className={css.navigation} aria-label="Показатели">
      {sections.map(section => <a key={section.id} href={`#${section.id}`}>{section.title}</a>)}
    </nav>

    <div className={css.sections}>
      {sections.map(section => <section id={section.id} key={section.id} className={css.section}>
        <h2>{section.title}</h2>
        <strong>{section.formula}</strong>
        <p>{section.note}</p>
      </section>)}
    </div>
  </main>
}

